
import { Component, OnInit, OnDestroy, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { BreadcrumbService } from '@k12/navigation';
import { AppInsightsService } from '@k12/error_handler';
import { AdminUserType, ProviderData } from '@k12/models';
import { IUserRecordEmployeeService, IUserRecordProviderDetailsService } from '@k12/tokens';
import { WORK_QUEUE_URL } from '@k12/admin-components';

const VIEW_EMPLOYEES_LABEL = 'View Employees';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-user-record-view-employees',
  imports: [MatButtonModule, MatIconModule, MatProgressBarModule],
  templateUrl: './user-record-view-employees.component.html'
})
export class UserRecordViewEmployeesComponent implements OnInit, OnDestroy {
  // Input for services that need to be injected from the consuming application
  @Input() employeeService!: IUserRecordEmployeeService;
  @Input() userRecordService!: IUserRecordProviderDetailsService;
  public personaId: number | null = null;
  public userId: string | null = null;
  public userData: ProviderData | null = null;
  private breadcrumbService = inject(BreadcrumbService);
  private router = inject(Router);
  private readonly appInsightsService = inject(AppInsightsService);

  public ngOnDestroy(): void {
    this.breadcrumbService.clearDynamicBreadcrumbs();
  }

  public ngOnInit(): void {
    if (history.state && history.state['personaId']) {
      this.personaId = history.state['personaId'];
    }

    if (history.state && history.state['providerId']) {
      this.userId = history.state['providerId'];
    }

    this.getUserDataForBreadcrumbs();
    this.getData();
    document.documentElement.scrollTop = 0;
  }

  getData(): void {
    if (!this.employeeService || !this.userId) return;
    const payload = {
      Filter: {
        PageNumber: 1,
        PageSize: 10
      }
    };
    this.employeeService.GetAllEmployeesSummaryAdmin(this.userId, payload).subscribe({
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  // Method to get user data and set breadcrumbs
  private getUserDataForBreadcrumbs(): void {
    const userId = this.userId;
    if (userId && this.userRecordService) {
      this.userRecordService.getProviderDetails(userId, AdminUserType.Provider).subscribe({
        next: (res: unknown) => {
          this.userData = res as ProviderData;
        },
        error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
      });
    }
    this.setBreadcrumbsForViewEmployees();
  }

  // Method to set breadcrumbs for the view employees page
  private setBreadcrumbsForViewEmployees(): void {
    if (this.userData && this.userData.firstName && this.userData.lastName && this.userId) {
      const userName = `${this.userData.firstName} ${this.userData.lastName}`;
      // Set breadcrumbs for user record and view employees pages
      this.breadcrumbService.setNestedDynamicBreadcrumbs(WORK_QUEUE_URL, this.userId, userName, undefined, VIEW_EMPLOYEES_LABEL);
    } else if (this.userRecordService) {
      const routeSegments = this.router.url.split('/');
      const userId = routeSegments[2];
      this.userRecordService.getProviderDetails(userId, AdminUserType.Provider).subscribe({
        next: (res: unknown) => {
          const data = res as ProviderData | null;
          if (data) {
            if (data.businessName) {
              this.breadcrumbService.setNestedDynamicBreadcrumbs(
                WORK_QUEUE_URL,
                userId,
                data.businessName,
                undefined,
                VIEW_EMPLOYEES_LABEL
              );
            } else if (data.firstName && data.lastName) {
              const userName = `${data.firstName} ${data.lastName}`;
              this.breadcrumbService.setNestedDynamicBreadcrumbs(
                WORK_QUEUE_URL,
                userId,
                userName,
                undefined,
                VIEW_EMPLOYEES_LABEL
              );
            }
          }
        },
        error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
      });
    }
  }
}

